import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { apiBaseUrl } from '@/constants/apiBaseUrl'
import { AssistantMarkdown } from 'components/assistant/AssistantMarkdown'
import {
  downloadTranscript,
  formatExportStamp,
  type TranscriptMessage,
} from 'components/assistant/assistantExport'

type ShareView = {
  title: string
  expiresAt: number
  messages: TranscriptMessage[]
}

type Gate =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'expired' }
  | { kind: 'passcode'; title: string; expiresAt: number; error: string }
  | { kind: 'ready'; share: ShareView }
  | { kind: 'error' }

async function requestShare(token: string, passcode?: string): Promise<Response> {
  const url = `${apiBaseUrl}/portal/public/assistant-share/${encodeURIComponent(token)}`
  if (!passcode) return fetch(url)
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ passcode }),
  })
}

function asShare(body: unknown): ShareView | null {
  if (!body || typeof body !== 'object') return null
  const title = (body as { title?: unknown }).title
  const expiresAt = (body as { expiresAt?: unknown }).expiresAt
  const messages = (body as { messages?: unknown }).messages
  if (typeof title !== 'string' || typeof expiresAt !== 'number' || !Array.isArray(messages)) return null
  const transcript = messages.flatMap((item): TranscriptMessage[] => {
    if (!item || typeof item !== 'object') return []
    const role = (item as { role?: unknown }).role
    const content = (item as { content?: unknown }).content
    if (typeof content !== 'string') return []
    if (role === 'user' || role === 'assistant') return [{ role, content }]
    return []
  })
  return { title, expiresAt, messages: transcript }
}

const PublicAssistantSharePage = () => {
  const { token = '' } = useParams()
  const [gate, setGate] = useState<Gate>({ kind: 'loading' })
  const [passcode, setPasscode] = useState('')
  const [busy, setBusy] = useState(false)

  const applyResponse = async (res: Response, enteredPasscode: boolean) => {
    if (res.status === 404) {
      setGate({ kind: 'not_found' })
      return
    }
    if (res.status === 410) {
      setGate({ kind: 'expired' })
      return
    }
    const body = (await res.json().catch(() => null)) as
      | { error?: string; title?: string; expiresAt?: number }
      | null
    if (res.status === 401 && body?.error === 'passcode_required') {
      setGate({
        kind: 'passcode',
        title: typeof body.title === 'string' ? body.title : 'Shared chat',
        expiresAt: typeof body.expiresAt === 'number' ? body.expiresAt : 0,
        error: '',
      })
      return
    }
    if (res.status === 401) {
      setGate({
        kind: 'passcode',
        title: typeof body?.title === 'string' ? body.title : 'Shared chat',
        expiresAt: typeof body?.expiresAt === 'number' ? body.expiresAt : 0,
        error: enteredPasscode ? 'That passcode is not right.' : '',
      })
      return
    }
    if (res.status === 429) {
      setGate({
        kind: 'passcode',
        title: typeof body?.title === 'string' ? body.title : 'Shared chat',
        expiresAt: typeof body?.expiresAt === 'number' ? body.expiresAt : 0,
        error: 'Too many attempts. Try again later.',
      })
      return
    }
    if (!res.ok) {
      setGate({ kind: 'error' })
      return
    }
    const share = asShare(body)
    if (!share) {
      setGate({ kind: 'error' })
      return
    }
    setGate({ kind: 'ready', share })
  }

  useEffect(() => {
    let cancelled = false
    setGate({ kind: 'loading' })
    void requestShare(token)
      .then(res => {
        if (!cancelled) return applyResponse(res, false)
        return undefined
      })
      .catch(() => {
        if (!cancelled) setGate({ kind: 'error' })
      })
    return () => {
      cancelled = true
    }
    // The first load only depends on the token in the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const unlock = async () => {
    if (!passcode.trim() || gate.kind !== 'passcode') return
    setBusy(true)
    try {
      const res = await requestShare(token, passcode.trim())
      await applyResponse(res, true)
    } catch {
      setGate({ ...gate, error: 'Could not open this link.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default', py: { xs: 2, md: 4 } }}>
      <Container maxWidth='md'>
        {gate.kind === 'loading' ? (
          <Typography color='text.secondary'>Opening shared chat…</Typography>
        ) : null}
        {gate.kind === 'not_found' ? (
          <Alert severity='warning'>This link is not valid.</Alert>
        ) : null}
        {gate.kind === 'expired' ? (
          <Alert severity='warning'>This link has expired.</Alert>
        ) : null}
        {gate.kind === 'error' ? (
          <Alert severity='error'>Could not open this link.</Alert>
        ) : null}
        {gate.kind === 'passcode' ? (
          <Paper sx={{ p: { xs: 2, md: 3 }, maxWidth: 420 }}>
            <Stack spacing={2}>
              <Typography variant='h6'>{gate.title}</Typography>
              <Typography variant='body2' color='text.secondary'>
                This chat needs a passcode
                {gate.expiresAt ? ` and is available until ${formatExportStamp(new Date(gate.expiresAt))}` : ''}.
              </Typography>
              <TextField
                label='Passcode'
                type='password'
                value={passcode}
                onChange={event => setPasscode(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') void unlock()
                }}
                error={Boolean(gate.error)}
                helperText={gate.error || ' '}
                fullWidth
                autoFocus
              />
              <Button variant='contained' disabled={busy || passcode.trim().length < 4} onClick={() => void unlock()}>
                {busy ? 'Checking…' : 'Open chat'}
              </Button>
            </Stack>
          </Paper>
        ) : null}
        {gate.kind === 'ready' ? (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='h6' noWrap>
                  {gate.share.title}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Available until {formatExportStamp(new Date(gate.share.expiresAt))}
                </Typography>
              </Box>
              <Stack direction='row' spacing={1}>
                <Button size='small' onClick={() => void downloadTranscript('pdf', gate.share.title, gate.share.messages)}>
                  PDF
                </Button>
                <Button size='small' onClick={() => void downloadTranscript('md', gate.share.title, gate.share.messages)}>
                  Markdown
                </Button>
                <Button size='small' onClick={() => void downloadTranscript('txt', gate.share.title, gate.share.messages)}>
                  TXT
                </Button>
              </Stack>
            </Stack>
            <Paper sx={{ p: { xs: 1.5, md: 2.5 } }}>
              {gate.share.messages.map((message, index) => (
                <Box key={`${message.role}-${index}`} sx={{ mb: 2 }}>
                  <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                    {message.role === 'user' ? 'You' : 'Assistant'}
                  </Typography>
                  {message.role === 'assistant' ? (
                    <AssistantMarkdown content={message.content} />
                  ) : (
                    <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                      {message.content}
                    </Typography>
                  )}
                </Box>
              ))}
            </Paper>
          </Stack>
        ) : null}
      </Container>
    </Box>
  )
}

export default PublicAssistantSharePage
